if (!customElements.get('arangara-tabs')) {
  customElements.define(
    'arangara-tabs',
    class extends HTMLElement {
      connectedCallback() {
        this.lifecycle = new AbortController();
        const options = { signal: this.lifecycle.signal };
        document.addEventListener(
          'shopify:section:load',
          (event) => {
            if (event.target.contains(this)) this.initialize();
          },
          options,
        );
        document.addEventListener(
          'shopify:block:select',
          (event) => this.onBlockSelect(event),
          options,
        );
        this.initialize();
      }

      disconnectedCallback() {
        this.lifecycle.abort();
        this.cleanup();
      }

      cleanup() {
        this.listeners?.abort();
        this.carousels?.forEach((api, wrapper) => {
          api.destroy();
          wrapper.removeAttribute('data-carousel-ready');
        });
      }

      initialize() {
        const previousId = this.buttons?.find(
          (button) => button.getAttribute('aria-selected') === 'true',
        )?.dataset.panel;
        this.cleanup();
        this.listeners = new AbortController();
        this.carousels = new Map();
        const options = { signal: this.listeners.signal };
        this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
        if (this.hasAttribute('data-generated-tabs')) this.buildNavigation();
        this.buttons = Array.from(this.querySelectorAll('[data-tab-button]'));
        this.buttons.forEach((button, index) => {
          const panel = this.panels.find((item) => item.id === button.dataset.panel);
          // Theme block settings belong to the block's own Liquid scope.
          const label =
            panel?.querySelector('.tab-item-title')?.textContent.trim() || `Tab ${index + 1}`;
          const labelElement = button.querySelector('.tab-item-title') || button;
          labelElement.textContent = label;
          button.addEventListener('click', () => this.selectPanel(button.dataset.panel), options);
          button.addEventListener('keydown', (event) => this.onKeyDown(event), options);
        });
        this.setAttribute('data-initialized', '');
        const navigation = this.querySelector('[data-tab-navigation]');
        if (navigation) navigation.hidden = this.buttons.length < 2;
        const selected = this.panels.find((panel) => panel.id === previousId) || this.panels[0];
        if (selected) this.selectPanel(selected.id);
      }

      buildNavigation() {
        const navigation = this.querySelector('[data-tab-navigation]');
        const tablist = navigation.querySelector('[role="tablist"]');
        const template = this.querySelector('[data-tab-template]');
        tablist.replaceChildren();
        this.panels.forEach((panel) => {
          const button = template.content.firstElementChild.cloneNode(true);
          button.id = `btn-${panel.id}`;
          button.dataset.tabButton = '';
          button.dataset.panel = panel.id;
          button.setAttribute('aria-controls', panel.id);
          tablist.append(button);
          if (this.panels.length > 1) {
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('aria-labelledby', button.id);
            panel.tabIndex = 0;
          } else {
            panel.removeAttribute('role');
            panel.removeAttribute('aria-labelledby');
            panel.removeAttribute('tabindex');
          }
        });
      }

      selectPanel(id) {
        const selected = this.panels.find((panel) => panel.id === id);
        if (!selected) return;
        this.buttons.forEach((button) => {
          const active = button.dataset.panel === id;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
          button.classList.toggle('active', active);
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel !== selected;
          panel.classList.toggle('selected', panel === selected);
          panel.classList.toggle('active', panel === selected);
        });
        selected
          .querySelectorAll('[data-carousel]')
          .forEach((wrapper) => this.initializeCarousel(wrapper));
      }

      initializeCarousel(wrapper) {
        if (this.carousels.has(wrapper)) {
          this.carousels.get(wrapper).reInit();
          return;
        }
        if (typeof EmblaCarousel !== 'function') return;
        const viewport = wrapper.querySelector('[data-carousel-viewport]');
        if (!viewport.firstElementChild?.children.length) return;
        wrapper.setAttribute('data-carousel-ready', '');
        const api = EmblaCarousel(viewport, {
          loop: false,
          align: 'start',
          containScroll: 'trimSnaps',
          direction: getComputedStyle(wrapper).direction === 'rtl' ? 'rtl' : 'ltr',
          duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 25,
        });
        this.carousels.set(wrapper, api);
        const previous = wrapper.querySelector('[data-carousel-previous]');
        const next = wrapper.querySelector('[data-carousel-next]');
        const controls = wrapper.querySelector('[data-carousel-controls]');
        const update = () => {
          previous.disabled = !api.canScrollPrev();
          next.disabled = !api.canScrollNext();
          previous.classList.toggle('disabled', previous.disabled);
          next.classList.toggle('disabled', next.disabled);
          controls.hidden = api.scrollSnapList().length < 2;
        };
        const options = { signal: this.listeners.signal };
        previous.addEventListener('click', () => api.scrollPrev(), options);
        next.addEventListener('click', () => api.scrollNext(), options);
        api.on('select', update).on('reInit', update);
        update();
      }

      onKeyDown(event) {
        const index = this.buttons.indexOf(event.currentTarget);
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        let next;
        if (event.key === 'ArrowRight') next = index + direction;
        else if (event.key === 'ArrowLeft') next = index - direction;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.buttons.length - 1;
        else return;
        event.preventDefault();
        next = (next + this.buttons.length) % this.buttons.length;
        this.selectPanel(this.buttons[next].dataset.panel);
        this.buttons[next].focus();
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const panel =
          event.target.closest('[data-tab-panel]') ||
          event.target.querySelector('[data-tab-panel]');
        if (!panel) return;
        this.selectPanel(panel.id);
        const slide = event.target.closest('[data-carousel-slide]');
        if (!slide) return;
        const api = this.carousels.get(slide.closest('[data-carousel]'));
        if (api) api.scrollTo(api.slideNodes().indexOf(slide), true);
      }
    },
  );
}
